import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Check, X, ChevronDown, Upload, AlertCircle, Search, Download } from "lucide-react";
import axios from "axios";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "sedes", label: "Sedes", endpoint: "/sedes", hasUnit: false, showUnit: false, showCodigo: false, showZona: false },
  { id: "zonas", label: "Zonas", endpoint: "/zonas", hasUnit: false, showUnit: false, showCodigo: false, showZona: false },
  { id: "pedidos", label: "Pedidos", endpoint: "/productos/pedidos", hasUnit: true, showUnit: false, showCodigo: false, showZona: true },
  { id: "inventario", label: "Inventario", endpoint: "/productos/inventario", hasUnit: true, showUnit: true, showCodigo: true, showZona: true },
  { id: "inventario_general", label: "Inventario General", endpoint: "/productos/inventario-general", hasUnit: true, showUnit: true, showCodigo: true, showZona: false },
];

const EMPTY_FORM = { name: "", unit: "", zona_id: "", sede_id: "", codigo: "" };

// ──────────────────────────────────────────────
// Selector de entidad con opción "Crear nueva"
// ──────────────────────────────────────────────
const EntitySelectField = ({
  label, value, onChange, items, onItemCreated,
  createEndpoint, placeholder, testIdPrefix,
}) => {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.post(`${API}${createEndpoint}`, { name: newName.trim() });
      onItemCreated(res.data);
      onChange(res.data.id);
      setNewName("");
      setShowCreate(false);
    } catch (err) {
      console.error("Error creando:", err);
    }
  };

  if (showCreate) {
    return (
      <div className="flex gap-2 items-center">
        <input
          autoFocus
          type="text"
          placeholder={`Nombre de la nueva ${label.toLowerCase()}`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          className="flex-1 h-11 bg-neutral-800 border border-emerald-400/50 rounded-md px-3 text-neutral-50 placeholder:text-neutral-600 text-sm focus:border-emerald-400 focus:outline-none"
        />
        <button
          onClick={handleCreate}
          disabled={!newName.trim()}
          className="h-11 px-3 bg-emerald-400 text-neutral-950 rounded-md flex items-center disabled:opacity-50"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          onClick={() => { setShowCreate(false); setNewName(""); }}
          className="h-11 px-3 bg-neutral-800 text-neutral-400 rounded-md flex items-center"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <select
        data-testid={`${testIdPrefix}-${label.toLowerCase()}`}
        value={value}
        onChange={(e) => {
          if (e.target.value === "__new__") setShowCreate(true);
          else onChange(e.target.value);
        }}
        className="w-full h-11 bg-neutral-800 border border-neutral-700 rounded-md px-3 pr-10 text-neutral-50 text-sm focus:border-emerald-400 focus:outline-none appearance-none"
      >
        <option value="">— Seleccionar {label.toLowerCase()} —</option>
        {items.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
        <option value="__new__">+ Crear nueva {label.toLowerCase()}...</option>
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
    </div>
  );
};

// ──────────────────────────────────────────────
// ──────────────────────────────────────────────
// Modal de previsualización y validación XLSX
// ──────────────────────────────────────────────
const ImportModal = ({ rows, validationReport, isImporting, tabId, onConfirm, onCancel, isValidating }) => {
  const isInventarioLike = tabId === "inventario" || tabId === "inventario_general";
  const showZona = tabId !== "inventario_general";
  
  // Si está en fase de validación o ya tiene reporte, muestra el reporte
  if (validationReport) {
    const hasErrors = validationReport.errors.length > 0;
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center px-4 pb-6">
        <div
          className="bg-neutral-900 border border-neutral-800 rounded-md w-full max-w-md flex flex-col"
          style={{ maxHeight: "75vh" }}
        >
          <div className="p-4 border-b border-neutral-800">
            <h2 className="text-neutral-50 font-semibold flex items-center gap-2">
              {hasErrors ? <AlertCircle className="w-5 h-5 text-red-500" /> : <Check className="w-5 h-5 text-emerald-500" />}
              Reporte de Validación
            </h2>
            <p className="text-neutral-500 text-sm mt-0.5">
              {validationReport.valid} válidos, {validationReport.errors.length} errores
            </p>
          </div>

          <div className="overflow-y-auto flex-1 px-4">
            {/* Mostrar errores si existen */}
            {validationReport.errors.length > 0 && (
              <div className="py-3">
                <p className="text-red-400 text-xs font-semibold mb-2">FILAS CON ERRORES:</p>
                {validationReport.errors.slice(0, 15).map((err, i) => (
                  <div key={i} className="py-2 border-l-2 border-red-500 pl-2 mb-2 text-xs">
                    <div className="text-neutral-50 font-mono">{err.row}</div>
                    <div className="text-red-400 text-xs mt-0.5">{err.message}</div>
                  </div>
                ))}
                {validationReport.errors.length > 15 && (
                  <p className="text-neutral-500 text-xs mt-2">... y {validationReport.errors.length - 15} más</p>
                )}
              </div>
            )}

            {/* Mostrar filas válidas si no hay muchas */}
            {!hasErrors && validationReport.valid > 0 && (
              <div className="py-3">
                <p className="text-emerald-400 text-xs font-semibold mb-2">LISTO PARA IMPORTAR:</p>
                {rows.slice(0, 15).map((row, i) => (
                  <div key={i} className="py-2 border-b border-neutral-800 last:border-0">
                    <div className="text-neutral-50 text-sm font-medium truncate">{row.producto}</div>
                    <div className="text-neutral-500 text-xs mt-0.5 font-mono">
                      {row.unidad}
                      {showZona && row.zona && <span className="ml-2 text-emerald-600">· {row.zona}</span>}
                      {isInventarioLike && row.codigo && (
                        <span className="ml-2 text-sky-600">· {row.codigo}</span>
                      )}
                    </div>
                  </div>
                ))}
                {rows.length > 15 && (
                  <p className="text-neutral-500 text-xs mt-2">... y {rows.length - 15} más</p>
                )}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-neutral-800 flex gap-2">
            <button
              onClick={onCancel}
              disabled={isImporting || isValidating}
              className="flex-1 h-10 bg-neutral-800 text-neutral-50 rounded-md text-sm font-medium hover:bg-neutral-700 disabled:opacity-50"
              data-testid="btn-cancel-import"
            >
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              disabled={isImporting || isValidating || hasErrors}
              className="flex-1 h-10 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              data-testid="btn-confirm-import"
            >
              {isImporting ? "Importando..." : hasErrors ? "Corregir errores" : "Importar"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fase de previsualización (si no hay reporte)
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center px-4 pb-6">
      <div
        className="bg-neutral-900 border border-neutral-800 rounded-md w-full max-w-md flex flex-col"
        style={{ maxHeight: "75vh" }}
      >
        <div className="p-4 border-b border-neutral-800">
          <h2 className="text-neutral-50 font-semibold">Revisar datos</h2>
          <p className="text-neutral-500 text-sm mt-0.5">
            {rows.length} productos a validar
          </p>
        </div>

        <div className="overflow-y-auto flex-1 px-4">
          {rows.slice(0, 30).map((row, i) => (
            <div key={i} className="py-2.5 border-b border-neutral-800 last:border-0">
              <div className="text-neutral-50 text-sm font-medium truncate">{row.producto}</div>
              <div className="text-neutral-500 text-xs mt-0.5 font-mono">
                {row.unidad}
                {showZona && row.zona && <span className="ml-2 text-emerald-600">· {row.zona}</span>}
                {isInventarioLike && row.codigo && (
                  <span className="ml-2 text-sky-600">· {row.codigo}</span>
                )}
              </div>
            </div>
          ))}
          {rows.length > 30 && (
            <p className="text-neutral-600 text-xs text-center py-3">
              ... y {rows.length - 30} producto{rows.length - 30 !== 1 ? "s" : ""} más
            </p>
          )}
        </div>

        <div className="p-4 border-t border-neutral-800 flex gap-2">
          <button
            data-testid="btn-cancel-import"
            onClick={onCancel}
            disabled={isValidating}
            className="flex-1 h-12 bg-neutral-800 text-neutral-400 rounded-md text-sm disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            data-testid="btn-validate-import"
            onClick={onConfirm}
            disabled={isValidating}
            className="flex-1 h-12 bg-emerald-400 text-neutral-950 rounded-md font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isValidating ? (
              <span>Validando...</span>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Validar {rows.length} productos
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Modal de exportación
// ──────────────────────────────────────────────
const ExportModal = ({ data, isExporting, onConfirm, onCancel }) => {
  const [sel, setSel] = useState({ sedes: true, zonas: true, pedidos: true, inventario: true, inventario_general: true });
  const toggle = (k) => setSel((p) => ({ ...p, [k]: !p[k] }));
  const anySelected = Object.values(sel).some(Boolean);
  const items = [
    { key: "sedes", label: "Sedes", count: data.sedes.length },
    { key: "zonas", label: "Zonas", count: data.zonas.length },
    { key: "pedidos", label: "Pedidos", count: data.pedidos.length },
    { key: "inventario", label: "Inventario", count: data.inventario.length },
    { key: "inventario_general", label: "Inventario General", count: data.inventario_general.length },
  ];
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center px-4 pb-6">
      <div className="bg-neutral-900 border border-neutral-800 rounded-md w-full max-w-sm">
        <div className="p-4 border-b border-neutral-800">
          <h2 className="text-neutral-50 font-semibold text-base">Exportar datos</h2>
          <p className="text-neutral-500 text-xs mt-0.5">Selecciona qué información exportar</p>
        </div>
        <div className="p-4 space-y-4">
          {items.map(({ key, label, count }) => (
            <label key={key} className="flex items-center justify-between cursor-pointer select-none">
              <div className="flex items-center gap-3">
                <input
                  data-testid={`export-check-${key}`}
                  type="checkbox"
                  checked={sel[key]}
                  onChange={() => toggle(key)}
                  className="w-4 h-4 rounded accent-emerald-400"
                />
                <span className="text-neutral-200 text-sm">{label}</span>
              </div>
              <span className="text-neutral-600 text-xs">{count} registro{count !== 1 ? "s" : ""}</span>
            </label>
          ))}
        </div>
        <div className="p-4 border-t border-neutral-800 flex gap-2">
          <button
            data-testid="btn-confirm-export"
            onClick={() => onConfirm(sel)}
            disabled={!anySelected || isExporting}
            className="flex-1 h-12 bg-emerald-400 text-neutral-950 rounded-md font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            {isExporting ? "Exportando..." : "Exportar XLSX"}
          </button>
          <button
            data-testid="btn-cancel-export"
            onClick={onCancel}
            disabled={isExporting}
            className="h-12 px-4 bg-neutral-800 text-neutral-400 rounded-md text-sm"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Página principal de administración
// ──────────────────────────────────────────────
const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("sedes");
  const [data, setData] = useState({ sedes: [], zonas: [], pedidos: [], inventario: [], inventario_general: [] });
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Pagination state: track page and total for each tab
  const [pagination, setPagination] = useState({
    sedes: { page: 1, limit: 50, total: 0 },
    zonas: { page: 1, limit: 50, total: 0 },
    pedidos: { page: 1, limit: 50, total: 0 },
    inventario: { page: 1, limit: 50, total: 0 },
    inventario_general: { page: 1, limit: 50, total: 0 },
  });

  // Items per page selector (default 50, max 100)
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Import state
  const [importRows, setImportRows] = useState([]);
  const [importError, setImportError] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationReport, setValidationReport] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState(""); // Tracks what search is actually running on server
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingXlsx, setIsExportingXlsx] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  const lastSearchQueryRef = useRef(""); // Track last executed search to avoid duplicates

  const activeTabConfig = TABS.find((t) => t.id === activeTab);

  // Zonas and sedes are reference data needed by product tabs — always keep them loaded
  const fetchReferenceData = async () => {
    try {
      const [sedesRes, zonasRes] = await Promise.all([
        axios.get(`${API}/sedes`, { params: { skip: 0, limit: 1000 } }),
        axios.get(`${API}/zonas`, { params: { skip: 0, limit: 1000 } }),
      ]);
      setData(prev => ({
        ...prev,
        sedes: sedesRes.data.items,
        zonas: zonasRes.data.items,
      }));
      setPagination(prev => ({
        ...prev,
        sedes: { ...prev.sedes, total: sedesRes.data.total },
        zonas: { ...prev.zonas, total: zonasRes.data.total },
      }));
    } catch (err) {
      console.error("Error cargando datos de referencia:", err);
    }
  };

  const fetchAll = async (tabId = activeTab, page = 1, query = searchQuery, limit = itemsPerPage) => {
    setLoading(true);
    try {
      const endpoint = TABS.find(t => t.id === tabId)?.endpoint;

      // Sedes and zonas are small reference sets — always load complete list (no pagination)
      const isRefTab = tabId === "sedes" || tabId === "zonas";
      const loadLimit = isRefTab ? 1000 : (query ? 1000 : limit);
      const skip = isRefTab || query ? 0 : (page - 1) * limit;

      const res = await axios.get(`${API}${endpoint}`, {
        params: { skip, limit: loadLimit }
      });

      // Update data with items from response
      setData(prev => ({
        ...prev,
        [tabId]: res.data.items
      }));

      // Update pagination metadata
      setPagination(prev => ({
        ...prev,
        [tabId]: { 
          ...prev[tabId], 
          page, 
          limit,
          total: res.data.total 
        }
      }));
    } catch (err) {
      console.error("Error cargando datos:", err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Load reference data (zonas/sedes) in parallel with the active tab on mount
    fetchReferenceData();
    if (activeTab !== "sedes" && activeTab !== "zonas") {
      fetchAll(activeTab, 1);
    }
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setShowAddForm(false);
    setEditingId(null);
    setAddForm(EMPTY_FORM);
    setImportError("");
    setImportResults(null);
    setSearchQuery("");
    setActiveSearchQuery(""); // Reset active search too
    lastSearchQueryRef.current = ""; // Reset search tracking
    fetchAll(tabId, 1, ""); // Load page 1 of new tab with no search
  };

  const handleSearchChange = (query) => {
    // Update input value immediately for local filtering — no fetch yet
    setSearchQuery(query);
  };

  const performSearch = (query = searchQuery) => {
    // Only fetch if the query actually changed from last search
    if (query === lastSearchQueryRef.current) return;
    
    lastSearchQueryRef.current = query;
    setActiveSearchQuery(query); // Update the search that's now active
    setPagination(prev => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], page: 1 }
    }));
    fetchAll(activeTab, 1, query);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      performSearch();
    }
  };

  const handleSearchBlur = () => {
    performSearch();
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    // Reset to page 1 and reload with new limit (pass it directly to avoid stale state)
    setPagination(prev => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], page: 1, limit: newLimit }
    }));
    fetchAll(activeTab, 1, searchQuery, newLimit);
  };

  const handleEntityCreated = (entity, field) => {
    setData((prev) => ({
      ...prev,
      [field]: [...prev[field], entity].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  };

  const buildPayload = (form) => {
    if (!activeTabConfig.hasUnit) return { name: form.name.trim() };
    const payload = {
      name: form.name.trim(),
      unit: form.unit?.trim() || null,
    };
    if (activeTabConfig.showZona) {
      payload.zona_id = form.zona_id || null;
    }
    if (activeTabConfig.showCodigo) {
      payload.codigo = form.codigo?.trim() || null;
    }
    return payload;
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}${activeTabConfig.endpoint}`, buildPayload(addForm));
      setAddForm(EMPTY_FORM);
      setShowAddForm(false);
      await fetchAll(activeTab, 1); // Reset to page 1 after adding
    } catch (err) {
      console.error("Error agregando:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id) => {
    if (!editForm.name.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}${activeTabConfig.endpoint}/${id}`, buildPayload(editForm));
      setEditingId(null);
      await fetchAll(activeTab, pagination[activeTab].page); // Keep current page
    } catch (err) {
      console.error("Error editando:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este elemento? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}${activeTabConfig.endpoint}/${id}`);
      await fetchAll(activeTab, pagination[activeTab].page); // Keep current page
    } catch (err) {
      console.error("Error eliminando:", err);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      name: item.name,
      unit: item.unit || "",
      zona_id: item.zona_id || "",
      sede_id: item.sede_id || "",
      codigo: item.codigo || "",
    });
    setShowAddForm(false);
  };

  const currentData = data[activeTab];

  // Only filter based on the ACTIVE search query (what the server searched), not the typed input
  const filteredCurrentData = currentData.filter((item) =>
    item.name.toLowerCase().includes(activeSearchQuery.toLowerCase())
  );

  // Exportar todos los datos como XLSX (siempre fetches completos, no depende de la paginación actual)
  const executeExport = async (selections) => {
    setIsExportingXlsx(true);
    try {
      // Fetch all data fresh in parallel for selected collections
      const fetchAll1000 = (endpoint) =>
        axios.get(`${API}${endpoint}`, { params: { skip: 0, limit: 1000 } })
          .then((r) => r.data.items ?? r.data);

      const [allSedes, allZonas, allPedidos, allInventario, allInvGeneral] = await Promise.all([
        fetchAll1000("/sedes"),
        fetchAll1000("/zonas"),
        selections.pedidos ? fetchAll1000("/productos/pedidos") : Promise.resolve([]),
        selections.inventario ? fetchAll1000("/productos/inventario") : Promise.resolve([]),
        selections.inventario_general ? fetchAll1000("/productos/inventario-general") : Promise.resolve([]),
      ]);

      // Build a zona lookup from the freshly fetched zonas
      const zonaLookup = (id) => allZonas.find((z) => z.id === id)?.name || "";

      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = "Sistema de Gestión";
      wb.created = new Date();
      const hStyle = {
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF16A34A" } },
        font: { bold: true, color: { argb: "FFFFFFFF" }, size: 11 },
        alignment: { vertical: "middle", horizontal: "center" },
        border: { bottom: { style: "thin", color: { argb: "FF166534" } } },
      };
      const addHeader = (ws, cols) => {
        ws.addRow(cols);
        ws.lastRow.height = 24;
        ws.lastRow.eachCell((c) => { c.style = hStyle; });
      };
      if (selections.sedes) {
        const ws = wb.addWorksheet("Sedes");
        ws.getColumn(1).width = 32;
        addHeader(ws, ["Nombre"]);
        allSedes.forEach((s) => ws.addRow([s.name]));
      }
      if (selections.zonas) {
        const ws = wb.addWorksheet("Zonas");
        ws.getColumn(1).width = 32;
        addHeader(ws, ["Nombre"]);
        allZonas.forEach((z) => ws.addRow([z.name]));
      }
      if (selections.pedidos) {
        const ws = wb.addWorksheet("Pedidos");
        ws.getColumn(1).width = 42; ws.getColumn(2).width = 28;
        addHeader(ws, ["Producto", "Zona"]);
        allPedidos.forEach((p) => ws.addRow([p.name, zonaLookup(p.zona_id)]));
      }
      if (selections.inventario) {
        const ws = wb.addWorksheet("Inventario");
        ws.getColumn(1).width = 42; ws.getColumn(2).width = 14;
        ws.getColumn(3).width = 18;
        ws.getColumn(4).width = 28;
        addHeader(ws, ["Producto", "Unidad", "Código", "Zona"]);
        allInventario.forEach((p) =>
          ws.addRow([p.name, p.unit || "", p.codigo || "", zonaLookup(p.zona_id)])
        );
      }
      if (selections.inventario_general) {
        const ws = wb.addWorksheet("Inventario General");
        ws.getColumn(1).width = 42;
        ws.getColumn(2).width = 18;
        addHeader(ws, ["Producto", "Código"]);
        allInvGeneral.forEach((p) =>
          ws.addRow([p.name, p.codigo || ""])
        );
      }
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gestion_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exportando:", err);
    } finally {
      setIsExportingXlsx(false);
      setShowExportModal(false);
    }
  };

  // ──────────────────────────────────────────────
  // XLSX Import logic
  // ──────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError("");
    setImportResults(null);

    try {
      const buffer = await file.arrayBuffer();
      const ExcelJS = (await import("exceljs")).default;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const ws = workbook.worksheets[0];
      if (!ws) throw new Error("El archivo no contiene hojas de cálculo.");

      // Lector robusto: maneja strings, números, rich text, fórmulas y errores
      const readCell = (cell) => {
        if (!cell) return "";
        const v = cell.value;
        if (v === null || v === undefined) return "";
        if (typeof v === "object") {
          // Resultado de fórmula: { formula: '...', result: 'und' }
          if (v.result !== undefined && v.result !== null) return String(v.result).trim();
          // Rich text: { richText: [{ text: 'und' }] }
          if (Array.isArray(v.richText)) return v.richText.map((r) => r.text ?? "").join("").trim();
          // Hipervínculo: { text: 'und', hyperlink: '...' }
          if (v.text !== undefined) return String(v.text).trim();
          // Error de celda → vacío
          if (v.error !== undefined) return "";
          return "";
        }
        return String(v).trim();
      };

      // Collect headers (row 1)
      const headerMap = {};
      ws.getRow(1).eachCell((cell, col) => {
        const val = readCell(cell).toLowerCase();
        if (val) headerMap[val] = col;
      });

      // Validate required headers
      const required =
        activeTab === "inventario"
          ? ["producto", "unidad", "codigo", "zona"]
          : activeTab === "inventario_general"
          ? ["producto", "codigo"]
          : ["producto", "zona"];

      const hasHeader = (h) => {
        if (h === "codigo") {
          return ("codigo" in headerMap) || ("código" in headerMap);
        }
        return h in headerMap;
      };

      const missing = required.filter((h) => !hasHeader(h));
      if (missing.length > 0) {
        const labels = missing.map((h) => (h === "codigo" ? "Código" : h.charAt(0).toUpperCase() + h.slice(1)));
        throw new Error(
          `Encabezados faltantes: ${labels.join(", ")}.\n` +
          `El archivo debe tener: ${required.map((h) => (h === "codigo" ? "Código" : h.charAt(0).toUpperCase() + h.slice(1))).join(" - ")}`
        );
      }

      // Parse data rows
      const rows = [];
      ws.eachRow((row, rowNum) => {
        if (rowNum === 1) return;
        const get = (key) => {
          const col = headerMap[key];
          if (!col) return "";
          return readCell(row.getCell(col));
        };
        const producto = get("producto");
        if (!producto) return; // skip empty rows
        rows.push({
          producto,
          unidad: get("unidad"),
          zona: get("zona"),
          codigo: get("código") || get("codigo"),
        });
      });

      if (rows.length === 0) throw new Error("El archivo no contiene filas de datos.");

      setImportRows(rows);
      setShowImportModal(true);
    } catch (err) {
      setImportError(err.message || "Error al leer el archivo.");
    }
  };

  // ──────────────────────────────────────────────
  // Two-phase import: validation + execution
  // ──────────────────────────────────────────────
  const validateRow = (row, rowNum) => {
    const errors = [];
    
    // SOLO validar que el producto (nombre) no esté vacío
    // Todos los otros campos (zona, unidad, código) son opcionales y pueden ser null
    if (!row.producto || !row.producto.trim()) {
      errors.push("Producto vacío (obligatorio)");
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      rowNum,
      rowData: row
    };
  };

  const executeValidation = async () => {
    setIsValidating(true);
    try {
      const results = importRows.map((row, idx) => validateRow(row, idx + 2)); // +2: headers + data rows start at 2
      const validRows = results.filter(r => r.isValid).length;
      const errorRows = results.filter(r => !r.isValid);
      
      const report = {
        valid: validRows,
        errors: errorRows.map(err => ({
          row: `Fila ${err.rowNum}: "${err.rowData.producto}"`,
          message: err.errors.join(", ")
        }))
      };
      
      setValidationReport(report);
    } finally {
      setIsValidating(false);
    }
  };

  const executeImport = async () => {
    // Fase 1: Si no hay validación, ejecutarla primero
    if (!validationReport) {
      await executeValidation();
      return;
    }

    // Fase 2: Si hay errores, no proceder
    if (validationReport.errors.length > 0) {
      return;
    }

    // Fase 3: Proceder con la importación
    setIsImporting(true);
    let created = 0;
    let failed = 0;

    try {
      // Snapshot local zonas and sedes to avoid repeated lookups
      let localZonas = [...data.zonas];
      let localSedes = [...data.sedes];
      const zonaCache = {}; // name.lower -> id
      const sedeCache = {}; // name.lower -> id

      const getOrCreateZona = async (name) => {
        const key = name.toLowerCase().trim();
        if (zonaCache[key]) return zonaCache[key];
        const existing = localZonas.find((z) => z.name.toLowerCase() === key);
        if (existing) { zonaCache[key] = existing.id; return existing.id; }
        try {
          const res = await axios.post(`${API}/zonas`, { name: name.trim() });
          localZonas.push(res.data);
          zonaCache[key] = res.data.id;
          return res.data.id;
        } catch { return null; }
      };

      const getOrCreateSede = async (name) => {
        const key = name.toLowerCase().trim();
        if (sedeCache[key]) return sedeCache[key];
        const existing = localSedes.find((s) => s.name.toLowerCase() === key);
        if (existing) { sedeCache[key] = existing.id; return existing.id; }
        try {
          const res = await axios.post(`${API}/sedes`, { name: name.trim() });
          localSedes.push(res.data);
          sedeCache[key] = res.data.id;
          return res.data.id;
        } catch { return null; }
      };

      // Solo importar filas que pasan validación
      for (const row of importRows) {
        const validation = validateRow(row);
        if (!validation.isValid) continue; // skip invalid rows

        try {
          const zona_id = activeTabConfig.showZona && row.zona ? await getOrCreateZona(row.zona) : null;

          const payload = {
            name: row.producto,
            // Campos opcionales: null si están vacíos
            unit: row.unidad?.trim() || null,
          };
          if (activeTabConfig.showZona) {
            payload.zona_id = zona_id;
          }
          if (activeTabConfig.showCodigo) {
            payload.codigo = row.codigo?.trim() || null;
          }

          await axios.post(`${API}${activeTabConfig.endpoint}`, payload);
          created++;
        } catch (err) {
          // Log detailed error for debugging
          console.error(`Error importando "${row.producto}":`, {
            status: err.response?.status,
            message: err.response?.data?.detail || err.message,
            error: err.response?.data || err
          });
          failed++;
        }
      }

      setImportResults({ created, failed });
    } finally {
      setIsImporting(false);
      setShowImportModal(false);
      setImportRows([]);
      setValidationReport(null);
      await fetchAll(activeTab, 1); // Reload to page 1 after import
    }
  };

  const getZonaName = (id) => data.zonas.find((z) => z.id === id)?.name || null;

  return (
    <div
      className="min-h-screen bg-neutral-950 flex flex-col max-w-md mx-auto"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <NavBar title="Administración" subtitle="Gestión de datos" />

      {/* Tabs + botón Exportar */}
      <div className="px-4 pt-4 pb-3 flex items-center gap-2">
        <div className="flex flex-1 bg-neutral-900 p-1 rounded-md gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 py-2.5 text-xs font-medium rounded transition-all duration-150 ${
                activeTab === tab.id
                  ? "bg-neutral-800 text-neutral-50"
                  : "text-neutral-500 active:bg-neutral-800 active:text-neutral-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          data-testid="btn-export-all"
          onClick={() => setShowExportModal(true)}
          className="h-10 w-10 shrink-0 bg-neutral-900 border border-neutral-800 rounded-md text-neutral-400 flex items-center justify-center active:bg-neutral-800 transition-colors"
          title="Exportar datos"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>

      {/* Add / Import buttons */}
      <div className="px-4 pb-3 space-y-2">
        {!showAddForm && (
          <div className={activeTabConfig?.hasUnit ? "flex gap-2" : ""}>
            <button
              data-testid="btn-add-item"
              onClick={() => { setShowAddForm(true); setEditingId(null); setAddForm(EMPTY_FORM); }}
              className={`h-12 border border-dashed border-neutral-700 rounded-md text-neutral-400 text-sm flex items-center justify-center gap-2 active:bg-neutral-900 transition-colors duration-150 ${
                activeTabConfig?.hasUnit ? "flex-1" : "w-full"
              }`}
            >
              <Plus className="w-4 h-4" />
              Agregar {activeTabConfig?.label}
            </button>

            {activeTabConfig?.hasUnit && (
              <button
                data-testid="btn-import-xlsx"
                onClick={() => { setImportError(""); fileInputRef.current?.click(); }}
                className="h-12 px-4 border border-dashed border-neutral-700 rounded-md text-neutral-400 text-sm flex items-center gap-2 active:bg-neutral-900 transition-colors duration-150"
                title="Importar desde XLSX"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Importar</span>
                <span className="sm:hidden">XLSX</span>
              </button>
            )}
          </div>
        )}

        {/* Import error */}
        {importError && (
          <div className="flex items-start gap-2 p-3 bg-rose-950/40 border border-rose-400/30 rounded-md">
            <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
            <p className="text-rose-300 text-xs whitespace-pre-line">{importError}</p>
          </div>
        )}

        {/* Import success */}
        {importResults && (
          <div className="p-3 bg-emerald-950/40 border border-emerald-400/30 rounded-md">
            <p className="text-emerald-300 text-xs">
              Importación completada: {importResults.created} creado{importResults.created !== 1 ? "s" : ""}
              {importResults.failed > 0 && `, ${importResults.failed} error${importResults.failed !== 1 ? "es" : ""}`}.
            </p>
          </div>
        )}

        {/* Add form */}
        {showAddForm && (
          <div className="bg-neutral-900 border border-emerald-400/30 rounded-md p-4 space-y-3">
            <input
              data-testid="input-add-name"
              type="text"
              placeholder={activeTabConfig.hasUnit ? "Nombre del producto" : "Nombre"}
              value={addForm.name}
              onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              className="w-full h-11 bg-neutral-800 border border-neutral-700 rounded-md px-3 text-neutral-50 placeholder:text-neutral-600 text-sm focus:border-emerald-400 focus:outline-none"
              autoFocus
            />
            {activeTabConfig.hasUnit && (
              <>
                {activeTabConfig.showUnit && (
                  <input
                    data-testid="input-add-unit"
                    type="text"
                    placeholder="Unidad (ej: ml, und, kg, lt)"
                    value={addForm.unit}
                    onChange={(e) => setAddForm((p) => ({ ...p, unit: e.target.value }))}
                    className="w-full h-11 bg-neutral-800 border border-neutral-700 rounded-md px-3 text-neutral-50 placeholder:text-neutral-600 text-sm focus:border-emerald-400 focus:outline-none"
                  />
                )}
                {activeTabConfig.showCodigo && (
                  <input
                    data-testid="input-add-codigo"
                    type="text"
                    placeholder="Código (ej: INV-001)"
                    value={addForm.codigo}
                    onChange={(e) => setAddForm((p) => ({ ...p, codigo: e.target.value }))}
                    className="w-full h-11 bg-neutral-800 border border-neutral-700 rounded-md px-3 text-neutral-50 placeholder:text-neutral-600 text-sm focus:border-emerald-400 focus:outline-none"
                  />
                )}
                {activeTabConfig.showZona && (
                  <div>
                    <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1.5 block">Zona</label>
                    <EntitySelectField
                      label="Zona"
                      value={addForm.zona_id}
                      onChange={(v) => setAddForm((p) => ({ ...p, zona_id: v }))}
                      items={data.zonas}
                      onItemCreated={(z) => handleEntityCreated(z, "zonas")}
                      createEndpoint="/zonas"
                      testIdPrefix="add"
                    />
                  </div>
                )}
                {/* sede_id guardado silenciosamente — no se expone en UI por ahora */}
              </>
            )}
            <div className="flex gap-2">
              <button
                data-testid="btn-save-add"
                onClick={handleAdd}
                disabled={saving || !addForm.name.trim()}
                className="flex-1 h-11 bg-emerald-400 text-neutral-950 rounded-md font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <Check className="w-4 h-4" />
                Guardar
              </button>
              <button
                data-testid="btn-cancel-add"
                onClick={() => { setShowAddForm(false); setAddForm(EMPTY_FORM); }}
                className="h-11 px-4 bg-neutral-800 text-neutral-400 rounded-md text-sm flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto px-4 pb-8">
        {loading ? (
          <div className="py-10 text-center text-neutral-500 text-sm">Cargando...</div>
        ) : currentData.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-neutral-500 text-sm">No hay elementos.</p>
            <p className="text-neutral-600 text-xs mt-1">Agrega el primero con el botón de arriba.</p>
          </div>
        ) : (
          <div>
            {/* Buscador */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <input
                data-testid="admin-search-input"
                type="text"
                placeholder={`Buscar en todos los ${activeTabConfig.label.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchBlur}
                className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-md pl-9 pr-9 text-neutral-50 placeholder:text-neutral-600 focus:border-emerald-400 focus:outline-none text-sm"
              />
              {searchQuery && (
                <button
                  data-testid="admin-search-clear"
                  onMouseDown={(e) => {
                    // Use onMouseDown instead of onClick to execute before blur
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchQuery("");
                    setActiveSearchQuery("");
                    lastSearchQueryRef.current = "";
                    setPagination(prev => ({
                      ...prev,
                      [activeTab]: { ...prev[activeTab], page: 1 }
                    }));
                    fetchAll(activeTab, 1, "");
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-neutral-500" />
                </button>
              )}
            </div>
            <div className="text-xs font-semibold tracking-widest uppercase text-neutral-600 pb-2">
              {activeSearchQuery
                ? `${filteredCurrentData.length} resultado${filteredCurrentData.length !== 1 ? "s" : ""} de ${pagination[activeTab].total} total`
                : `${pagination[activeTab].total} elemento${pagination[activeTab].total !== 1 ? "s" : ""} en total`}
            </div>
            {filteredCurrentData.length === 0 && activeSearchQuery ? (
              <div className="py-8 text-center text-neutral-500 text-sm">
                Sin resultados para "{activeSearchQuery}".
              </div>
            ) : (
            filteredCurrentData.map((item) => (
              <div key={item.id} data-testid={`admin-item-${item.id}`} className="border-b border-neutral-800">
                {editingId === item.id ? (
                  <div className="py-3 space-y-2">
                    <input
                      data-testid={`input-edit-name-${item.id}`}
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && handleEdit(item.id)}
                      className="w-full h-11 bg-neutral-800 border border-emerald-400/50 rounded-md px-3 text-neutral-50 text-sm focus:border-emerald-400 focus:outline-none"
                      autoFocus
                    />
                    {activeTabConfig.hasUnit && (
                      <>
                        {activeTabConfig.showUnit && (
                          <input
                            data-testid={`input-edit-unit-${item.id}`}
                            type="text"
                            value={editForm.unit}
                            onChange={(e) => setEditForm((p) => ({ ...p, unit: e.target.value }))}
                            className="w-full h-11 bg-neutral-800 border border-neutral-700 rounded-md px-3 text-neutral-50 text-sm focus:border-emerald-400 focus:outline-none"
                          />
                        )}
                        {activeTabConfig.showCodigo && (
                          <input
                            data-testid={`input-edit-codigo-${item.id}`}
                            type="text"
                            placeholder="Código (ej: INV-001)"
                            value={editForm.codigo}
                            onChange={(e) => setEditForm((p) => ({ ...p, codigo: e.target.value }))}
                            className="w-full h-11 bg-neutral-800 border border-neutral-700 rounded-md px-3 text-neutral-50 text-sm focus:border-emerald-400 focus:outline-none"
                          />
                        )}
                        {activeTabConfig.showZona && (
                          <div>
                            <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1.5 block">Zona</label>
                            <EntitySelectField
                              label="Zona"
                              value={editForm.zona_id}
                              onChange={(v) => setEditForm((p) => ({ ...p, zona_id: v }))}
                              items={data.zonas}
                              onItemCreated={(z) => handleEntityCreated(z, "zonas")}
                              createEndpoint="/zonas"
                              testIdPrefix={`edit-${item.id}`}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex gap-2">
                      <button
                        data-testid={`btn-save-edit-${item.id}`}
                        onClick={() => handleEdit(item.id)}
                        disabled={saving || !editForm.name.trim()}
                        className="flex-1 h-10 bg-emerald-400 text-neutral-950 rounded-md font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-1"
                      >
                        <Check className="w-3.5 h-3.5" />Guardar
                      </button>
                      <button
                        data-testid={`btn-cancel-edit-${item.id}`}
                        onClick={() => setEditingId(null)}
                        className="h-10 px-4 bg-neutral-800 text-neutral-400 rounded-md text-sm flex items-center gap-1"
                      >
                        <X className="w-3.5 h-3.5" />Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 py-3.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-neutral-50 text-sm font-medium truncate">{item.name}</div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {activeTabConfig.showUnit && item.unit && (
                          <span className="text-neutral-500 text-xs font-mono-data">{item.unit}</span>
                        )}
                        {activeTabConfig.showCodigo && item.codigo && (
                          <>
                            {activeTabConfig.showUnit && item.unit && <span className="text-neutral-700 text-xs">·</span>}
                            <span className="text-sky-600 text-xs font-mono-data">{item.codigo}</span>
                          </>
                        )}
                        {activeTabConfig.showZona && item.zona_id && getZonaName(item.zona_id) && (
                          <>
                            {(activeTabConfig.showUnit && item.unit) || (activeTabConfig.showCodigo && item.codigo) ? (
                              <span className="text-neutral-700 text-xs">·</span>
                            ) : null}
                            <span className="text-emerald-600 text-xs">{getZonaName(item.zona_id)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid={`btn-edit-${item.id}`}
                      onClick={() => startEdit(item)}
                      className="p-2 text-neutral-600 active:text-neutral-300 transition-colors rounded"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`btn-delete-${item.id}`}
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-neutral-600 active:text-rose-400 transition-colors rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))
            )}

            {/* Pagination Controls */}
            {!activeSearchQuery && currentData.length > 0 && activeTab !== "sedes" && activeTab !== "zonas" && (
              <div className="pt-4 border-t border-neutral-800 space-y-3">
                {/* Items per page selector */}
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span>Elementos por página</span>
                  <div className="flex gap-1">
                    {[10, 25, 50, 100].map(n => (
                      <button
                        key={n}
                        onClick={() => handleItemsPerPageChange(n)}
                        className={`w-10 h-7 rounded text-xs transition-colors ${
                          itemsPerPage === n
                            ? "bg-emerald-600 text-white"
                            : "bg-neutral-800 text-neutral-400 active:bg-neutral-700"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Prev/Next buttons — only if there are multiple pages */}
                {pagination[activeTab].total > itemsPerPage && (
                  <div className="flex items-center justify-between text-xs">
                    <div className="text-neutral-500">
                      Página {pagination[activeTab].page} de {Math.ceil(pagination[activeTab].total / itemsPerPage)}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => fetchAll(activeTab, pagination[activeTab].page - 1)}
                        disabled={pagination[activeTab].page === 1 || loading}
                        className="px-3 h-7 bg-neutral-800 text-neutral-50 rounded text-xs disabled:opacity-30"
                      >
                        ← Ant.
                      </button>
                      <button
                        onClick={() => fetchAll(activeTab, pagination[activeTab].page + 1)}
                        disabled={pagination[activeTab].page >= Math.ceil(pagination[activeTab].total / itemsPerPage) || loading}
                        className="px-3 h-7 bg-neutral-800 text-neutral-50 rounded text-xs disabled:opacity-30"
                      >
                        Sig. →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          data={data}
          isExporting={isExportingXlsx}
          onConfirm={executeExport}
          onCancel={() => setShowExportModal(false)}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          rows={importRows}
          validationReport={validationReport}
          isImporting={isImporting}
          isValidating={isValidating}
          tabId={activeTab}
          onConfirm={executeImport}
          onCancel={() => { 
            setShowImportModal(false); 
            setImportRows([]); 
            setValidationReport(null);
          }}
        />
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        data-testid="file-input-xlsx"
        onChange={handleFileSelect}
        onClick={(e) => { e.target.value = ""; }}
      />
    </div>
  );
};

export default AdminPage;
